import React from 'react';
import { CheckCircle, CircleNotch, Warning, X, XCircle } from '@phosphor-icons/react';
import { Progress } from '../../components/ui/progress';

const STAGE_LABELS = {
  upload_received: 'Upload received — queued for scanning',
  rendering: 'Preparing pages…',
  extracting: 'AI reading pages…',
  finalizing: 'Finalizing results…',
  complete: 'All pages ready for review',
  partial: 'Finished — some pages failed and can be retried',
  failed: 'All pages failed OCR — please retry',
};

const TerminalIcon = ({ stage }) => {
  if (stage === 'complete') return <CheckCircle size={18} className="text-emerald-600" weight="fill" />;
  if (stage === 'partial') return <Warning size={18} className="text-amber-500" weight="fill" />;
  if (stage === 'failed') return <XCircle size={18} className="text-red-600" weight="fill" />;
  return <CircleNotch size={18} className="animate-spin text-indigo-600" />;
};

/**
 * Live progress panel for background OCR upload batches. Server-backed:
 * polls the tenant-scoped `/timesheets/batch/{id}/status` endpoint every
 * 2s while processing, stops on terminal states, throttles when the tab
 * is hidden, and survives page refreshes (batch ids in sessionStorage).
 */
const BatchProgressPanel = ({ batches, onDismiss }) => {
  const list = Object.values(batches || {});
  if (list.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="batch-progress-panel">
      {list.map((b) => {
        const stage = b.stage || 'upload_received';
        const terminal = ['complete', 'partial', 'failed'].includes(stage);
        const label =
          stage === 'extracting' && b.pages_total
            ? `AI reading pages — ${b.pages_done || 0} of ${b.pages_total} done`
            : STAGE_LABELS[stage] || 'Processing…';
        return (
          <div
            key={b.batch_id}
            className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm"
            data-testid={`batch-progress-${b.batch_id}`}
          >
            <div className="flex items-center gap-2">
              <TerminalIcon stage={stage} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate" data-testid="batch-progress-filename">
                  {b.filename || 'Uploaded document'}
                </p>
                <p className="text-xs text-slate-500" data-testid="batch-progress-stage">
                  {label}
                </p>
              </div>
              <span className="text-xs font-semibold text-slate-600 shrink-0" data-testid="batch-progress-percent">
                {b.percent ?? 0}%
              </span>
              <button
                type="button"
                onClick={() => onDismiss?.(b.batch_id)}
                title="Dismiss"
                aria-label="Dismiss progress"
                className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"
                data-testid={`batch-progress-dismiss-${b.batch_id}`}
              >
                <X size={14} />
              </button>
            </div>
            <Progress
              value={b.percent ?? 0}
              className={`h-1.5 mt-2 ${terminal && stage !== 'complete' ? 'opacity-60' : ''}`}
            />
          </div>
        );
      })}
    </div>
  );
};

export default BatchProgressPanel;
